import { useBroadcastChannel } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { AI_FIRST_RESPONSE_TIMEOUT_MS } from '../constants/timeout'
import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'
import { useContactsStore } from './contacts'

export interface CallTranscriptMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: number
}

export interface CallLog {
  id: string
  characterId: string
  characterName: string
  characterAvatar?: string
  type: 'voice' | 'video'
  startTime: number
  duration: number
  initiator: 'user' | 'ai'
  transcript: CallTranscriptMessage[]
}

export interface ActiveCallState {
  isActive: boolean
  type: 'voice' | 'video' | null
  startTime: number | null
  characterId: string | null
  characterName: string | null
  characterAvatar: string | null
  transcript: CallTranscriptMessage[]
  initiator: 'user' | 'ai' | null
  pendingAssistantMessages?: CallTranscriptMessage[]
}

// 📡 BroadcastChannel 事件类型定义
type BroadcastChannelEvents
  = | { type: 'incoming_call', data: { callType: 'voice' | 'video', characterId: string, characterName: string, characterAvatar?: string } }
    | { type: 'call_started', data: { characterId: string, callType: 'voice' | 'video' } }
    | { type: 'call_ended', data: { characterId: string, duration: number } }
    | { type: 'call_accepted', data: { characterId: string } }
    | { type: 'call_rejected', data: { characterId: string } }
    | { type: 'call_cancelled', data: { characterId: string } }

const SESSION_KEY = 'meowchat_active_call'

export const useCallStore = defineStore('call', () => {
  // 📡 跨页面通信 - BroadcastChannel
  const { post: broadcastCall, data: receivedCall } = useBroadcastChannel<
    BroadcastChannelEvents,
    BroadcastChannelEvents
  >({ name: 'meowchat-calls' })

  // 当前通话状态
  const isCallActive = ref(false)
  const callType = ref<'voice' | 'video' | null>(null)
  const callStartTime = ref<number | null>(null)
  const callCharacterId = ref<string | null>(null)
  const callCharacterName = ref<string | null>(null)
  const callCharacterAvatar = ref<string | null>(null)
  const callInitiator = ref<'user' | 'ai' | null>(null)
  const currentTranscript = ref<CallTranscriptMessage[]>([])

  // 呼出状态（等待对方接听）
  const outgoingCallPending = ref(false)

  // 呼出超时定时器
  const outgoingCallTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)

  // 来电状态
  const incomingCallOffer = ref<{
    type: 'voice' | 'video'
    characterId: string
    characterName: string
    characterAvatar?: string
  } | null>(null)

  // AI主动发起通话时携带的问候语（用户接听后直接使用，无需二次请求）
  const pendingGreeting = ref<string | null>(null)

  // ✨ 通话记录列表 - 迁移到 IndexedDB（含完整对话转录，数据量可能很大）
  const callLogsStorage = useIndexedDBStorage<CallLog[]>('meowchat_call_logs', [], {
    debounceMs: 500,
  })
  const callLogs = callLogsStorage.data

  // 等待存储加载完成
  const callLogsLoaded = ref(false)
  waitForStorage(callLogsStorage).then(() => {
    callLogsLoaded.value = true
    console.debug('✅ 通话记录存储加载完成（IndexedDB）')
  })

  const pendingAssistantMessages = ref<CallTranscriptMessage[]>([])

  const contactsStore = useContactsStore()

  const shouldDelayAssistantMessages = computed(() => {
    if (!callCharacterId.value)
      return false
    const contact = contactsStore.getContact(callCharacterId.value)
    return Boolean(contact?.metadata?.voiceId)
  })

  // 计算属性：当前通话时长（秒）
  const currentCallDuration = computed(() => {
    if (!isCallActive.value || !callStartTime.value)
      return 0
    return Math.floor((Date.now() - callStartTime.value) / 1000)
  })

  // 格式化时长
  const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    const pad = (num: number) => String(num).padStart(2, '0')

    if (h > 0) {
      return `${pad(h)}:${pad(m)}:${pad(s)}`
    }
    return `${pad(m)}:${pad(s)}`
  }

  // 保存通话记录（IndexedDB 自动持久化）
  const saveCallLog = (log: CallLog) => {
    callLogs.value = [log, ...callLogs.value] // 添加到列表开头
  }

  // 加载通话记录（IndexedDB 自动加载，保留接口兼容性）
  const loadCallLogs = () => {
    // IndexedDB 存储会自动加载，此函数保留以兼容现有调用
    console.debug('📞 通话记录由 IndexedDB 自动管理')
  }

  // 删除通话记录
  const deleteCallLog = (logId: string) => {
    callLogs.value = callLogs.value.filter(log => log.id !== logId)
  }

  // 批量删除通话记录
  const deleteCallLogs = (logIds: string[]) => {
    const idSet = new Set(logIds)
    callLogs.value = callLogs.value.filter(log => !idSet.has(log.id))
  }

  // 获取某个角色的通话记录
  const getCallLogsByCharacter = (characterId: string) => {
    return callLogs.value.filter(log => log.characterId === characterId)
  }

  // 保存通话状态到 sessionStorage
  const saveCallStateToSession = () => {
    if (!isCallActive.value)
      return

    const state: ActiveCallState = {
      isActive: isCallActive.value,
      type: callType.value,
      startTime: callStartTime.value,
      characterId: callCharacterId.value,
      characterName: callCharacterName.value,
      characterAvatar: callCharacterAvatar.value,
      transcript: currentTranscript.value,
      initiator: callInitiator.value,
      pendingAssistantMessages: pendingAssistantMessages.value,
    }

    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
    }
    catch (error) {
      console.error('Failed to save call state to session:', error)
    }
  }

  // 重置通话状态
  const resetCallState = () => {
    isCallActive.value = false
    callType.value = null
    callStartTime.value = null
    callCharacterId.value = null
    callCharacterName.value = null
    callCharacterAvatar.value = null
    callInitiator.value = null
    currentTranscript.value = []
    outgoingCallPending.value = false
    incomingCallOffer.value = null
    pendingGreeting.value = null
    pendingAssistantMessages.value = []

    // 清除 sessionStorage
    try {
      sessionStorage.removeItem(SESSION_KEY)
    }
    catch (error) {
      console.error('Failed to remove session storage:', error)
    }
  }

  // 开始通话
  const startCall = (
    type: 'voice' | 'video',
    characterId: string,
    characterName: string,
    characterAvatar?: string,
    initiator: 'user' | 'ai' = 'user',
  ) => {
    isCallActive.value = true
    callType.value = type
    callStartTime.value = Date.now()
    callCharacterId.value = characterId
    callCharacterName.value = characterName
    callCharacterAvatar.value = characterAvatar || null
    callInitiator.value = initiator
    currentTranscript.value = []
    outgoingCallPending.value = false

    // 清除呼出超时定时器（通话已接通）
    if (outgoingCallTimeoutId.value) {
      clearTimeout(outgoingCallTimeoutId.value)
      outgoingCallTimeoutId.value = null
    }

    // 保存到 sessionStorage（用于页面刷新恢复）
    saveCallStateToSession()

    // 📡 广播通话开始事件到其他页面
    broadcastCall({
      type: 'call_started',
      data: { characterId, callType: type },
    })
  }

  // 取消呼出
  const cancelOutgoingCall = () => {
    const characterId = callCharacterId.value
    outgoingCallPending.value = false
    callType.value = null
    callCharacterId.value = null
    callCharacterName.value = null
    callCharacterAvatar.value = null

    // 清除超时定时器
    if (outgoingCallTimeoutId.value) {
      clearTimeout(outgoingCallTimeoutId.value)
      outgoingCallTimeoutId.value = null
    }

    // 📡 广播取消呼出事件到其他页面
    if (characterId) {
      broadcastCall({
        type: 'call_cancelled',
        data: { characterId },
      })
    }
  }

  // 设置呼出等待状态
  const setOutgoingCallPending = (
    type: 'voice' | 'video',
    characterId: string,
    characterName: string,
    characterAvatar?: string,
  ) => {
    outgoingCallPending.value = true
    callType.value = type
    callCharacterId.value = characterId
    callCharacterName.value = characterName
    callCharacterAvatar.value = characterAvatar || null

    // 🔔 设置超时：如果AI没有响应，自动取消呼出（使用全局首字超时配置）
    if (outgoingCallTimeoutId.value) {
      clearTimeout(outgoingCallTimeoutId.value)
    }
    outgoingCallTimeoutId.value = setTimeout(() => {
      if (outgoingCallPending.value) {
        console.warn(`⏰ [CallStore] 呼出超时（${AI_FIRST_RESPONSE_TIMEOUT_MS / 1000}秒无响应），自动取消`)
        cancelOutgoingCall()
      }
    }, AI_FIRST_RESPONSE_TIMEOUT_MS)
  }

  const normalizeText = (text: string) => text.replace(/\s+/g, '').trim()

  function pushMessageToTranscript(message: CallTranscriptMessage) {
    const contentPreview = typeof message.content === 'string' ? message.content.substring(0, 50) : String(message.content).substring(0, 50)
    console.info('📞 [CallStore] 添加消息到transcript:', contentPreview)
    console.info('📞 [CallStore] 添加前长度:', currentTranscript.value.length)
    currentTranscript.value.push(message)
    console.info('📞 [CallStore] 添加后长度:', currentTranscript.value.length)
    console.info('📞 [CallStore] 当前transcript:', currentTranscript.value.map((m) => {
      const content = typeof m.content === 'string' ? m.content : String(m.content)
      return `${m.role}: ${content.substring(0, 30)}`
    }))
    saveCallStateToSession()
  }

  // 添加消息到通话转录
  const addToTranscript = (message: CallTranscriptMessage) => {
    const shouldDelay = message.role === 'assistant' && shouldDelayAssistantMessages.value

    if (shouldDelay) {
      console.info('📞 [CallStore] 劫持助手消息，等待语音播放后再显示')
      pendingAssistantMessages.value.push(message)
      saveCallStateToSession()
      return
    }

    pushMessageToTranscript(message)
  }

  function releasePendingAssistantMessageByText(text?: string) {
    if (pendingAssistantMessages.value.length === 0)
      return

    let index = -1
    if (text) {
      const normalizedTarget = normalizeText(text)
      index = pendingAssistantMessages.value.findIndex(item => normalizeText(item.content) === normalizedTarget)
    }

    if (index === -1)
      index = 0

    const [message] = pendingAssistantMessages.value.splice(index, 1)
    const contentPreview = typeof message.content === 'string' ? message.content.substring(0, 50) : String(message.content).substring(0, 50)
    console.info('📞 [CallStore] 释放等待中的助手消息:', contentPreview)
    pushMessageToTranscript(message)
  }

  function flushPendingAssistantMessages() {
    if (pendingAssistantMessages.value.length === 0)
      return

    console.info('📞 [CallStore] 批量释放等待中的助手消息:', pendingAssistantMessages.value.length)
    const pending = [...pendingAssistantMessages.value]
    pendingAssistantMessages.value = []
    pending.forEach(pushMessageToTranscript)
  }

  // 结束通话
  const endCall = (): CallLog | null => {
    if (!isCallActive.value || !callStartTime.value || !callCharacterId.value) {
      // 重置状态
      resetCallState()
      return null
    }

    const duration = Math.floor((Date.now() - callStartTime.value) / 1000)

    flushPendingAssistantMessages()

    // 创建通话记录
    const callLog: CallLog = {
      id: crypto.randomUUID(),
      characterId: callCharacterId.value,
      characterName: callCharacterName.value || '未知',
      characterAvatar: callCharacterAvatar.value || undefined,
      type: callType.value!,
      startTime: callStartTime.value,
      duration,
      initiator: callInitiator.value!,
      transcript: currentTranscript.value,
    }

    // 保存记录
    saveCallLog(callLog)

    // 重置状态
    resetCallState()

    // 📡 广播通话结束事件到其他页面
    broadcastCall({
      type: 'call_ended',
      data: { characterId: callLog.characterId, duration },
    })

    return callLog
  }

  // 从 sessionStorage 恢复通话状态
  const restoreCallStateFromSession = () => {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY)
      if (!stored)
        return false

      const state: ActiveCallState = JSON.parse(stored)

      if (state.isActive && state.characterId) {
        isCallActive.value = state.isActive
        callType.value = state.type
        callStartTime.value = state.startTime
        callCharacterId.value = state.characterId
        callCharacterName.value = state.characterName
        callCharacterAvatar.value = state.characterAvatar
        currentTranscript.value = state.transcript || []
        pendingAssistantMessages.value = state.pendingAssistantMessages || []
        callInitiator.value = state.initiator

        return true
      }
    }
    catch (error) {
      console.error('Failed to restore call state from session:', error)
    }

    return false
  }

  // 设置来电
  const setIncomingCall = (
    type: 'voice' | 'video',
    characterId: string,
    characterName: string,
    characterAvatar?: string,
    greeting?: string,
  ) => {
    incomingCallOffer.value = {
      type,
      characterId,
      characterName,
      characterAvatar,
    }
    // 存储AI发起通话时附带的问候语
    pendingGreeting.value = greeting || null

    // 📡 广播来电事件到其他页面
    broadcastCall({
      type: 'incoming_call',
      data: { callType: type, characterId, characterName, characterAvatar },
    })
  }

  // 接听来电
  const acceptIncomingCall = (characterAvatar?: string) => {
    if (!incomingCallOffer.value)
      return false

    const characterId = incomingCallOffer.value.characterId
    startCall(
      incomingCallOffer.value.type,
      characterId,
      incomingCallOffer.value.characterName,
      characterAvatar || incomingCallOffer.value.characterAvatar, // 优先使用传入的头像，否则使用来电时的头像
      'ai',
    )
    incomingCallOffer.value = null

    // 📡 广播接听事件到其他页面
    broadcastCall({
      type: 'call_accepted',
      data: { characterId },
    })

    return true
  }

  // 拒绝来电
  const rejectIncomingCall = () => {
    if (!incomingCallOffer.value)
      return

    const characterId = incomingCallOffer.value.characterId
    incomingCallOffer.value = null

    // 📡 广播拒绝来电事件到其他页面
    broadcastCall({
      type: 'call_rejected',
      data: { characterId },
    })
  }

  // 📡 监听跨页面通话广播
  watch(receivedCall, (event) => {
    if (!event)
      return

    console.info('📨 收到通话广播:', event.type)

    switch (event.type) {
      case 'incoming_call':
        console.info('📞 其他页面收到来电')
        // 来电会通过 setIncomingCall 自动同步
        break

      case 'call_started':
        console.info('📞 其他页面开始通话')
        // 通话状态会通过 LocalStorage/SessionStorage 自动同步
        break

      case 'call_ended':
        console.info('📞 其他页面结束通话')
        // 通话记录会通过 LocalStorage 自动同步
        break

      case 'call_accepted':
        console.info('📞 其他页面接听了来电')
        break

      case 'call_rejected':
        console.info('📞 其他页面拒绝了来电')
        break

      case 'call_cancelled':
        console.info('📞 其他页面取消了呼出')
        break
    }
  })

  watch(shouldDelayAssistantMessages, (value) => {
    if (!value)
      flushPendingAssistantMessages()
  })

  /**
   * 清理某个联系人的所有相关引用（通话孤儿数据）
   * - 删除该联系人的所有通话记录
   * - 若当前/来电/呼出指向该联系人，强制重置
   */
  const purgeByContactId = (contactId: string) => {
    if (!contactId)
      return

    const before = callLogs.value.length
    callLogs.value = callLogs.value.filter(log => log.characterId !== contactId)

    if (incomingCallOffer.value?.characterId === contactId) {
      incomingCallOffer.value = null
      pendingGreeting.value = null
    }

    const isActiveOrPendingForContact
      = (isCallActive.value || outgoingCallPending.value)
        && callCharacterId.value === contactId

    if (isActiveOrPendingForContact)
      resetCallState()

    if (before !== callLogs.value.length)
      console.info(`🧹 [call] 清理联系人 ${contactId} 的引用，剩余 ${callLogs.value.length} 条记录`)
  }

  // 截断通话转录（从指定位置开始删除）
  const truncateTranscriptFrom = (index: number) => {
    if (index < 0 || index >= currentTranscript.value.length)
      return
    currentTranscript.value.splice(index, currentTranscript.value.length - index)
    saveCallStateToSession()
  }

  return {
    // 状态
    isCallActive,
    callType,
    callStartTime,
    callCharacterId,
    callCharacterName,
    callCharacterAvatar,
    callInitiator,
    currentTranscript,
    pendingAssistantMessages,
    outgoingCallPending,
    incomingCallOffer,
    pendingGreeting,
    callLogs,

    // 计算属性
    currentCallDuration,
    shouldDelayAssistantMessages,

    // 方法
    formatDuration,
    startCall,
    setOutgoingCallPending,
    cancelOutgoingCall,
    addToTranscript,
    endCall,
    resetCallState,
    saveCallLog,
    loadCallLogs,
    deleteCallLog,
    deleteCallLogs,
    purgeByContactId,
    getCallLogsByCharacter,
    saveCallStateToSession,
    restoreCallStateFromSession,
    setIncomingCall,
    acceptIncomingCall,
    rejectIncomingCall,
    releasePendingAssistantMessageByText,
    flushPendingAssistantMessages,
    truncateTranscriptFrom,
  }
})
